import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, X } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { StickyActionBar } from "@/components/layout/StickyActionBar";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { getUsuario, saveUsuario } from "./api";
import { usuarioFormSchema, type UsuarioFormValues } from "./schema";

// Porte de app/controllers/form-usuario.controller.js +
// app/templates/usuario/form-usuario.html. Nota sobre o legado: o
// input[type=hidden] "tipoUsuario" com value="OPERADOR" no template NÃO tem
// ng-model — nunca chega a $scope.registro, é markup morto. O valor real
// enviado é "SILPI", vindo de `$scope.registro = { tipoUsuario: "SILPI" }` no
// controller. Reproduzido aqui como "SILPI" (o comportamento de fato, não o
// que o HTML sugere).
export default function FormUsuario() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

  const form = useForm<UsuarioFormValues>({
    resolver: zodResolver(usuarioFormSchema),
    defaultValues: { nome: "", email: "", usuario: "", senha: "", confirmacaoSenha: "" },
  });

  const { data: registro } = useQuery({
    queryKey: ["usuario", "detail", id],
    queryFn: () => getUsuario(Number(id)),
    enabled: isEdit,
  });

  useEffect(() => {
    if (registro) {
      form.reset({ ...registro, senha: "", confirmacaoSenha: "" });
    }
  }, [registro, form]);

  const salvar = useMutation({
    mutationFn: (values: UsuarioFormValues) => {
      // `registro` primeiro: o zod descarta chaves fora do schema (roles,
      // situacao, auditoria...) e o legado devolvia a entidade inteira.
      const payload: Record<string, unknown> = { ...registro, ...values, tipoUsuario: "SILPI" };
      delete payload.confirmacaoSenha;
      if (!payload.senha) delete payload.senha;
      return saveUsuario(payload);
    },
    onSuccess: () => {
      toast.success("Usuário salvo.");
      qc.invalidateQueries({ queryKey: ["usuario"] });
      navigate("/Usuario/Consulta");
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit((values) => salvar.mutate(values))} className="space-y-6 pb-20">
        <h1 className="text-lg font-semibold text-foreground">Cadastro de Usuário</h1>

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="nome"
            render={({ field }) => (
              <FormItem className="sm:col-span-2">
                <FormLabel>Nome</FormLabel>
                <FormControl>
                  <Input autoFocus {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input type="email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="usuario"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Usuário</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="senha"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Senha</FormLabel>
                <FormControl>
                  <Input type="password" autoComplete="new-password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirmacaoSenha"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirmação da Senha</FormLabel>
                <FormControl>
                  <Input type="password" autoComplete="new-password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <StickyActionBar>
          <Button type="submit" disabled={salvar.isPending}>
            {salvar.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {salvar.isPending ? "Salvando..." : "Salvar"}
          </Button>
          <Button type="button" variant="outline" onClick={() => setConfirmCancelOpen(true)}>
            <X className="mr-2 h-4 w-4" /> Sair
          </Button>
        </StickyActionBar>
      </form>

      <ConfirmDialog
        open={confirmCancelOpen}
        onOpenChange={setConfirmCancelOpen}
        title="Cancelar alterações?"
        description="As alterações não salvas serão perdidas."
        confirmLabel="Sair sem salvar"
        onConfirm={() => navigate("/Usuario/Consulta")}
      />
    </Form>
  );
}
